/**
 * V2 admin — Lists tab (user ask 2026-07-19; follows the Yoinks tab
 * patterns).
 *
 * Every list — including private ones, via the admin read policy in
 * supabase/migrations/20260719a_admin_lists_url.sql — searchable and sortable, 25 per page.
 * The owner cell is live: tap to transfer the list to another explorer,
 * exactly like moving a yoink. "Want to go" system lists stay put (every
 * explorer has at most one; transferring would collide with the
 * target's).
 */
import * as gateway from '../../core/gateway.js';
import { invalidate } from '../../core/query.js';
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { confirmDialog } from './confirm.js';
import { fetchAll } from './data.js';
import { fetchAllCategories } from '../../core/categories.js';

const PER_PAGE = 25;

const VISIBILITY_LABEL = { private: '🔒 Private', unlisted: '🔗 Unlisted', public: '🌍 Public' };

/** Search + sort (same shape as filterYoinks). Exported for tests. */
export function filterLists(lists, { search = '', sortColumn = 'created_at', sortDirection = 'desc' } = {}) {
  let result = [...lists];
  if (search) {
    const term = search.toLowerCase();
    result = result.filter((l) =>
      (l.title || '').toLowerCase().includes(term) ||
      l.ownerName.toLowerCase().includes(term) ||
      (l.description || '').toLowerCase().includes(term) ||
      (l.visibility || '').toLowerCase().includes(term) ||
      (l.categoryName || '').toLowerCase().includes(term));
  }
  result.sort((a, b) => {
    const pick = (l) => {
      switch (sortColumn) {
        case 'title': return (l.title || '').toLowerCase();
        case 'owner': return l.ownerName.toLowerCase();
        case 'visibility': return (l.visibility || '').toLowerCase();
        case 'places': return l.entry_count;
        default: return new Date(l.created_at).getTime();
      }
    };
    const aVal = pick(a);
    const bVal = pick(b);
    if (sortColumn === 'created_at' || sortColumn === 'places') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  return result;
}

export function mount(el, ctx) {
  const { router } = ctx;
  let destroyed = false;
  let all = [];
  let profiles = []; // owner names + the transfer picker roster
  let loaded = false;
  let search = '';
  let sortColumn = 'created_at';
  let sortDirection = 'desc';
  let page = 1;
  let picker = null; // { listId, term, kind: 'owner' | 'category' }
  let featuredUnavailable = false; // 20260804b not applied → column hidden
  let categoryUnavailable = false; // 20260813f not applied → column hidden
  let cats = []; // the site catalog, for the category cell + picker

  async function load() {
    const select = 'id,owner_id,title,slug,description,visibility,kind,created_at,list_entries(count)';
    const [lists, users, catalog] = await Promise.all([
      // `url`, `featured` and `category_id` each predate their migrations
      // in some environments — fall back column by column, newest first
      fetchAll('lists', { select: `${select},url,featured,category_id`, order: 'created_at.desc' })
        .catch(() => {
          categoryUnavailable = true;
          return fetchAll('lists', { select: `${select},url,featured`, order: 'created_at.desc' });
        })
        .catch(() => {
          featuredUnavailable = true;
          return fetchAll('lists', { select: `${select},url`, order: 'created_at.desc' });
        })
        .catch(() => fetchAll('lists', { select, order: 'created_at.desc' })),
      fetchAll('profiles', { select: 'id,full_name,username', order: 'full_name.asc' }),
      fetchAllCategories().catch(() => []),
    ]);
    profiles = users.map((u) => ({ ...u, displayName: u.full_name || u.username || '(No name)' }));
    cats = catalog;
    const byId = new Map(profiles.map((u) => [u.id, u]));
    const catById = new Map(cats.map((c) => [c.id, c]));
    all = lists.map((l) => ({
      ...l,
      entry_count: l.list_entries?.[0]?.count ?? 0,
      ownerName: byId.get(l.owner_id)?.displayName || 'Unknown',
      ownerUsername: byId.get(l.owner_id)?.username || null,
      categoryName: (l.category_id && catById.get(l.category_id)?.name) || null,
    }));
    loaded = true;
  }

  const arrow = (col) => (sortColumn === col ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '');

  function shown() {
    return filterLists(all, { search, sortColumn, sortDirection });
  }

  function pickerListHtml() {
    const term = (picker.term || '').toLowerCase();
    if (picker.kind === 'category') {
      // The admin override for the owner-set list category (20260813f) —
      // the moderation door for a miscategorized public list. Same
      // can_tag rule as every category picker.
      const list = all.find((l) => l.id === picker.listId);
      const matches = cats.filter((c) => c.can_tag !== false)
        .filter((c) => !term || c.name.toLowerCase().includes(term));
      const noneRow = `
        <button type="button" class="admin-transfer-row" data-pick-listcat="">
          <span>— No category</span>
          ${list?.category_id ? '' : '<span class="admin-cell-dim">(current)</span>'}
        </button>`;
      if (!matches.length) return `${noneRow}<p class="admin-muted">No categories match.</p>`;
      return noneRow + matches.map((c) => `
        <button type="button" class="admin-transfer-row" data-pick-listcat="${c.id}">
          <span>${c.emoji || '📍'} ${escapeHtml(c.name)}</span>
          ${c.id === list?.category_id ? '<span class="admin-cell-dim">(current)</span>' : ''}
        </button>`).join('');
    }
    const users = profiles.filter((u) => !term || u.displayName.toLowerCase().includes(term));
    if (!users.length) return '<p class="admin-muted">No explorers match.</p>';
    const current = all.find((l) => l.id === picker.listId)?.owner_id;
    return users.map((u) => `
      <button type="button" class="admin-transfer-row" data-pick-owner="${u.id}" data-pick-name="${escapeHtml(u.displayName)}">
        <span>${escapeHtml(u.displayName)}</span>
        ${u.id === current ? '<span class="admin-cell-dim">(current)</span>' : ''}
      </button>`).join('');
  }

  function render() {
    if (!loaded) {
      el.innerHTML = '<div class="v2-loading">Unrolling the field notebooks…</div>';
      return;
    }
    const list = shown();
    const totalPages = Math.ceil(list.length / PER_PAGE) || 1;
    if (page > totalPages) page = totalPages;
    const pageRows = list.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    el.innerHTML = `
      <div class="admin-card admin-card-span">
        <div class="admin-toolbar">
          <input type="search" class="admin-search" data-list-search aria-label="Search lists" placeholder="Search lists…"
            value="${escapeHtml(search)}" autocomplete="off">
          <span class="admin-muted">${list.length} of ${all.length}</span>
          <span class="admin-toolbar-spacer"></span>
          <button type="button" class="admin-btn" data-page-prev ${page <= 1 ? 'disabled' : ''}>←</button>
          <span class="admin-muted">Page ${page} of ${totalPages}</span>
          <button type="button" class="admin-btn" data-page-next ${page >= totalPages ? 'disabled' : ''}>→</button>
        </div>
        <div class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                ${[['title', 'Title'], ['owner', 'Owner'], ['visibility', 'Visibility'],
                  ['places', 'Places'], ['created_at', 'Created']]
                  .map(([col, label]) => `<th class="admin-sortable" data-sort-lists="${col}">${label}${arrow(col)}</th>`).join('')}
                ${categoryUnavailable ? '' : '<th title="Public lists with a category surface in its search and on its page">Category</th>'}
                ${featuredUnavailable ? '' : '<th title="On the Discover shelf — public lists only">★</th>'}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${pageRows.length ? pageRows.map((l) => `
                <tr>
                  <td class="admin-cell-clamp">🔖 ${escapeHtml(l.title)}
                    ${l.kind === 'want_to_go' ? '<span class="admin-cell-dim">(system)</span>' : ''}
                    ${l.url ? `<a href="${escapeHtml(l.url)}" target="_blank" rel="noopener" aria-label="Open list link">🔗</a>` : ''}</td>
                  <td>${l.kind === 'want_to_go'
                    ? `<span class="admin-cell-dim">${escapeHtml(l.ownerName)}</span>`
                    : `<button type="button" class="admin-link" data-pick-list="${l.id}">${escapeHtml(l.ownerName)} ▾</button>`}</td>
                  <td class="admin-cell-dim">${VISIBILITY_LABEL[l.visibility] || escapeHtml(l.visibility || '')}</td>
                  <td>${l.entry_count}</td>
                  <td class="admin-cell-dim">${new Date(l.created_at).toLocaleDateString(undefined, { timeZone: 'America/Los_Angeles' })}</td>
                  ${categoryUnavailable ? '' : `<td>
                    <button type="button" class="admin-link" data-pick-list-category="${l.id}">
                      ${l.categoryName ? escapeHtml(l.categoryName) : '<span class="admin-cell-dim">—</span>'} ▾</button>
                  </td>`}
                  ${featuredUnavailable ? '' : `<td>
                    <input type="checkbox" data-feature-list="${l.id}" ${l.featured ? 'checked' : ''}
                      ${l.visibility === 'public' ? '' : 'disabled title="Only public lists can be featured"'}
                      aria-label="Feature ${escapeHtml(l.title)} on Discover">
                  </td>`}
                  <td>
                    <div class="admin-actions">
                      <button type="button" class="admin-btn admin-btn-xs" data-view-list="${l.id}"
                        ${l.ownerUsername ? '' : 'disabled title="Owner has no username — no list URL"'}>View</button>
                      <button type="button" class="admin-btn admin-btn-xs admin-btn-danger"
                        data-delete-list="${l.id}" data-list-title="${escapeHtml(l.title)}">Delete</button>
                    </div>
                  </td>
                </tr>`).join('') : `<tr><td colspan="${6 + (featuredUnavailable ? 0 : 1) + (categoryUnavailable ? 0 : 1)}" class="admin-empty-cell">No lists found</td></tr>`}
            </tbody>
          </table>
        </div>
      </div>
      ${picker ? `
      <dialog class="v2-auth-dialog admin-transfer-dialog" data-picker-dialog>
        <h2 class="v2-dialog-title">${picker.kind === 'category' ? 'File under a category' : 'Move to another explorer'}</h2>
        <input type="search" data-picker-search aria-label="Search" placeholder="Search…" autocomplete="off"
          value="${escapeHtml(picker.term || '')}">
        <div class="admin-transfer-list" data-picker-list>${pickerListHtml()}</div>
        <div class="admin-confirm-actions">
          <button type="button" class="admin-btn" data-picker-cancel>Cancel</button>
        </div>
      </dialog>` : ''}`;

    const dialog = el.querySelector('[data-picker-dialog]');
    if (dialog && typeof dialog.showModal === 'function') dialog.showModal();

    el.querySelector('[data-list-search]')?.addEventListener('input', debounce((e) => {
      const caret = e.target.selectionStart;
      search = e.target.value;
      page = 1;
      render();
      const again = el.querySelector('[data-list-search]');
      if (again) { again.focus(); again.setSelectionRange(caret, caret); }
    }, 300));
    el.querySelector('[data-picker-search]')?.addEventListener('input', debounce((e) => {
      picker.term = e.target.value;
      const listEl = el.querySelector('[data-picker-list]');
      if (listEl) listEl.innerHTML = pickerListHtml();
    }, 200));
  }

  async function transferList(listId, newOwnerId, newOwnerName) {
    const list = all.find((l) => l.id === listId);
    if (!list || list.owner_id === newOwnerId) { picker = null; return render(); }
    if (!(await confirmDialog({
      title: `Transfer "${list.title}"?`,
      message: `It will belong to ${newOwnerName} from now on.`,
      confirmLabel: 'Transfer',
    }))) return;
    const { data, error } = await gateway.rawUpdate(gateway.TABLES.LISTS, {
      filters: { id: `eq.${listId}` }, body: { owner_id: newOwnerId },
    });
    if (destroyed) return;
    if (error) {
      // owner+slug unique: the target already has a list with this slug
      return showToast(/HTTP 409/.test(error.message)
        ? `${newOwnerName} already has a list named like this.`
        : 'Couldn’t transfer the list.', { type: 'info' });
    }
    if (!data?.length) return showToast('Couldn’t transfer the list — is the admin SQL applied?', { type: 'info' });
    list.owner_id = newOwnerId;
    list.ownerName = newOwnerName;
    list.ownerUsername = profiles.find((u) => u.id === newOwnerId)?.username || null;
    picker = null;
    showToast('List transferred.', { type: 'success' });
    render();
  }

  /**
   * Feature/unfeature (2026-08-04). The DB-side freeze trigger is the
   * real guard; the returned row is the truth (a non-admin's write comes
   * back unchanged). Invalidate so Discover's door and the shelf reload.
   */
  async function toggleFeatured(listId, on) {
    const { data, error } = await gateway.rawUpdate(gateway.TABLES.LISTS, {
      filters: { id: `eq.${listId}` }, body: { featured: on },
    });
    if (destroyed) return;
    if (error || !data?.length) {
      showToast('Couldn’t update — is 20260804b_featured_lists.sql applied?', { type: 'info' });
      return render();
    }
    const list = all.find((l) => l.id === listId);
    if (list) list.featured = data[0].featured ?? on;
    invalidate();
    showToast(data[0].featured ? 'On the Featured shelf.' : 'Off the shelf.', { type: 'success' });
    render();
  }

  /**
   * File a list under a category, or clear it (catId null). The owner
   * can set this too — this is the moderation override. The returned row
   * is the truth, same as toggleFeatured.
   */
  async function setListCategory(listId, catId) {
    const { data, error } = await gateway.rawUpdate(gateway.TABLES.LISTS, {
      filters: { id: `eq.${listId}` }, body: { category_id: catId },
    });
    if (destroyed) return;
    picker = null;
    if (error || !data?.length) {
      showToast('Couldn’t update — is 20260813f_lists_category.sql applied?', { type: 'info' });
      return render();
    }
    const list = all.find((l) => l.id === listId);
    if (list) {
      list.category_id = data[0].category_id ?? catId;
      list.categoryName = (list.category_id && cats.find((c) => c.id === list.category_id)?.name) || null;
    }
    invalidate(); // the category page's shelf + search results reload
    showToast(list?.categoryName ? `Filed under ${list.categoryName}.` : 'Category cleared.', { type: 'success' });
    render();
  }

  async function deleteListRow(listId, title) {
    if (!(await confirmDialog({
      title: `Delete "${title}"?`,
      message: 'The list and its saved places are gone for good.',
    }))) return;
    const { error } = await gateway.rawDelete(gateway.TABLES.LISTS, {
      filters: { id: `eq.${listId}` },
    });
    if (destroyed) return;
    if (error) return showToast('Couldn’t delete the list.', { type: 'info' });
    all = all.filter((l) => l.id !== listId);
    showToast('List deleted.', { type: 'success' });
    render();
  }

  function onClick(event) {
    const t = event.target;
    const sortTh = t.closest('[data-sort-lists]');
    if (sortTh) {
      const col = sortTh.dataset.sortLists;
      if (sortColumn === col) sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      else { sortColumn = col; sortDirection = col === 'created_at' || col === 'places' ? 'desc' : 'asc'; }
      page = 1;
      return render();
    }
    if (t.closest('[data-page-prev]')) { if (page > 1) { page--; render(); } return; }
    if (t.closest('[data-page-next]')) { page++; return render(); }
    const pickBtn = t.closest('[data-pick-list]');
    if (pickBtn) {
      picker = { listId: pickBtn.dataset.pickList, term: '', kind: 'owner' };
      render();
      setTimeout(() => el.querySelector('[data-picker-search]')?.focus(), 50);
      return;
    }
    const pickCatBtn = t.closest('[data-pick-list-category]');
    if (pickCatBtn) {
      picker = { listId: pickCatBtn.dataset.pickListCategory, term: '', kind: 'category' };
      render();
      setTimeout(() => el.querySelector('[data-picker-search]')?.focus(), 50);
      return;
    }
    if (t.closest('[data-picker-cancel]')) {
      el.querySelector('[data-picker-dialog]')?.close?.();
      picker = null;
      return render();
    }
    const pickOwner = t.closest('[data-pick-owner]');
    if (pickOwner) return transferList(picker.listId, pickOwner.dataset.pickOwner, pickOwner.dataset.pickName);
    const pickListCat = t.closest('[data-pick-listcat]');
    if (pickListCat) return setListCategory(picker.listId, pickListCat.dataset.pickListcat || null);
    const viewBtn = t.closest('[data-view-list]');
    if (viewBtn) {
      const list = all.find((l) => l.id === viewBtn.dataset.viewList);
      if (list?.ownerUsername) router.navigate('list', { username: list.ownerUsername, slug: list.slug });
      return;
    }
    const deleteBtn = t.closest('[data-delete-list]');
    if (deleteBtn) return deleteListRow(deleteBtn.dataset.deleteList, deleteBtn.dataset.listTitle);
    const featureBox = t.closest('[data-feature-list]');
    if (featureBox) return toggleFeatured(featureBox.dataset.featureList, featureBox.checked);
  }

  render();
  load().then(() => { if (!destroyed) render(); })
    .catch(() => { if (!destroyed) el.innerHTML = '<div class="v2-error">Couldn’t load the lists.</div>'; });

  el.addEventListener('click', onClick);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
  };
}
